import * as React from 'react';

import {
  BillSortOption,
  enumBillSortOption,
  enumBillStatus,
} from '@rematter/frontend-lib-api-client';
import { FinanceRootLayout } from '@rematter/modules/finance/_components/_layouts/FinanceRootLayout';
import {
  DataGrid,
  ListScreenLayout,
  Pagination,
  type PaginationProps,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { billVoidAtom } from '../../_graphql/mutationBillVoid';
import * as state from '../../_state/financeBills.state';
import { financeRoutes } from '../../routes';

import { BillWizard } from './_BillWizard';
import { BillPaymentDrawer } from './BillPaymentDrawer';
import { BillsListActions } from './BillsListActions';
import { BillsListFilterBar } from './BillsListFilterBar';
import { BillsListFloatingToolbar } from './BillsListFloatingToolbar';
import { useBillsListColumns } from './hooks/useBillsListColumns';

import { useTranslations } from '@/finance/i18n/useTranslations';
import { BillRecord } from '~/modules/finance/types';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const COLUMN_TO_SORT_MAP: Record<
  string,
  { asc: BillSortOption; desc: BillSortOption }
> = {
  documentDate: {
    asc: enumBillSortOption.DocumentDateAsc,
    desc: enumBillSortOption.DocumentDateDesc,
  },
  status: {
    asc: enumBillSortOption.StatusAsc,
    desc: enumBillSortOption.StatusDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ BILLS LIST VIEW ⚛️ ⚛️ ⚛️
//
export const BillsListView = () => {
  // Translations
  const t = useTranslations();

  // Navigation
  const [, navigate] = useModuleLocation();

  // Atoms
  const {
    data: { bills, total } = { bills: [], total: 0 },
    error,
    refetch: refetchBills,
    isFetching,
    loadingState,
  } = useAtomValue(state.$remoteBills);
  const viewMode = useAtomValue(state.$viewMode);
  const [selectedKeys, setSelectedKeys] = useAtom(state.$selectedKeys);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [isPaymentDrawerOpen, setIsPaymentDrawerOpen] = useAtom(
    state.$isPaymentDrawerOpen,
  );
  const [billsToPay, setBillsToPay] = useAtom(state.$billsToPay);
  const [isWizardOpen, setIsWizardOpen] = useAtom(state.$isBillWizardOpen);
  const setCurrentSorting = useSetAtom(state.$currentSorting);
  const [columnSelectedStatuses, setColumnSelectedStatuses] = useAtom(
    state.$columnSelectedStatuses,
  );

  // Data hooks
  const toast = useToast();

  // Memoized values
  const $voidBill = React.useMemo(
    () =>
      billVoidAtom({
        onSuccess: () => {
          refetchBills?.();
          setSelectedKeys(new Set());
        },
      }),
    [refetchBills, setSelectedKeys],
  );
  const voidBill = useSetAtom($voidBill);

  const emptyPanelProps = React.useMemo(
    () => ({
      title: t.bills.list.title,
      description: t.bills.list.search.placeholder,
      centered: true,
    }),
    [t],
  );

  // Callbacks
  const handleVoid = React.useCallback(
    async (bill: BillRecord) => {
      if (bill.status === 'Voided' || bill.status === 'Paid') {
        toast.error(t.bills.list.tooltips.cannotVoid);
        return;
      }
      try {
        await voidBill({ id: bill.id });
        toast.success(t.bills.detail.messages.voided);
      } catch (err) {
        toast.error(
          t.bills.detail.messages.voidError(
            err instanceof Error ? err.message : t.messages.common.unknownError,
          ),
        );
      }
    },
    [voidBill, toast, t],
  );

  const handleClosePaymentDrawer = React.useCallback(() => {
    setIsPaymentDrawerOpen(false);
    setBillsToPay([]);
  }, [setIsPaymentDrawerOpen, setBillsToPay]);

  const handleWizardClose = React.useCallback(() => {
    setIsWizardOpen(false);
  }, [setIsWizardOpen]);

  const handlePaginationChange = React.useCallback<
    NonNullable<PaginationProps['onChange']>
  >(
    ({ limit: newLimit, offset: newOffset }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );

  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumBillSortOption.DocumentDateDesc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          setCurrentSorting([
            direction === 'ascending' ? sortMap.asc : sortMap.desc,
          ]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );

  const handleBillClick = React.useCallback(
    (bill: BillRecord) => {
      navigate(financeRoutes.billDetail, {
        params: { id: bill.id },
      });
    },
    [navigate],
  );

  // Effects
  React.useEffect(() => {
    if (columnSelectedStatuses.size === 0) {
      setColumnSelectedStatuses(new Set([enumBillStatus.Pending]));
    }
  }, []);

  const { columns: BILL_COLUMNS } = useBillsListColumns({
    setBillsToPay,
    setIsPaymentDrawerOpen,
    onVoid: handleVoid,
    onDoubleClick: handleBillClick,
    grouped: viewMode === 'grouped',
  });

  return (
    <FinanceRootLayout className="@BillsListView">
      <ListScreenLayout
        filters={<BillsListFilterBar />}
        actions={<BillsListActions />}
        footer={
          (total ?? 0) > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<BillRecord>
          columns={BILL_COLUMNS}
          dataSource={bills}
          rowKey="id"
          aria-label={t.bills.list.title}
          selectionMode="multiple"
          selectedKeys={selectedKeys}
          onSelectionChange={setSelectedKeys}
          onColumnSort={handleColumnSort}
          fullWidth
          isLoading={loadingState?.isInitialLoading || isFetching}
          hasError={!!error}
          groupBy={
            viewMode === 'grouped'
              ? (bill) => bill.accountId ?? '__no_account__'
              : undefined
          }
          errorPanelProps={
            error
              ? {
                  description: t.bills.list.error(error.message),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={emptyPanelProps}
        />
      </ListScreenLayout>

      <BillsListFloatingToolbar />

      <BillPaymentDrawer
        isOpen={isPaymentDrawerOpen}
        onClose={handleClosePaymentDrawer}
        bills={billsToPay ?? []}
        onSuccess={() => refetchBills?.()}
      />

      <BillWizard
        isOpen={isWizardOpen}
        onClose={handleWizardClose}
        onSuccess={() => refetchBills?.()}
      />
    </FinanceRootLayout>
  );
};
